import { Module, MutationTree, ActionTree, GetterTree } from "vuex"
import { RootState } from "@/store"

export interface ToastMessage {
    id: string
    message: string
    description?: string
    title?: string
    color: string
    delay: number
    isTranslation?: boolean
    link?: string
    linkText?: string
}

export interface ToastMessageState {
    messages: ToastMessage[]
}

const state: ToastMessageState = {
    messages: [],
}

const mutations: MutationTree<ToastMessageState> = {
    ADD_MESSAGE(state, payload: ToastMessage) {
        state.messages.push(payload)
    },
    REMOVE_MESSAGE(state, messageId: string) {
        state.messages = state.messages.filter((msg) => msg.id !== messageId)
    },
    CLEAR_ALL_MESSAGES(state) {
        state.messages = []
    },
}

const actions: ActionTree<ToastMessageState, RootState> = {
    showMessage(
        { commit, state },
        payload: {
            message: string
            description?: string
            title?: string
            color?: string
            delay?: number
            isTranslation?: boolean
            link?: string
            linkText?: string
        },
    ) {
        const alreadyShown = state.messages.some(
            (m) => m.message === payload.message && m.title === payload.title,
        )
        if (alreadyShown) return

        const id = Date.now().toString()
        const toastMessage: ToastMessage = {
            id,
            message: payload.message,
            description: payload.description,
            title: payload.title,
            color: payload.color || "var(--theme-tertiary)",
            delay: payload.delay ?? 5000,
            isTranslation: payload.isTranslation || false,
            ...(payload.link && { link: payload.link, linkText: payload.linkText }),
        }

        commit("ADD_MESSAGE", toastMessage)

        if (toastMessage.delay > 0) {
            setTimeout(() => {
                commit("REMOVE_MESSAGE", id)
            }, toastMessage.delay)
        }
    },
    showPermanentMessage({ commit }, payload: { message: string; color: string }) {
        const id = Date.now().toString()
        const toastMessage: ToastMessage = {
            id,
            message: payload.message,
            color: payload.color,
            delay: 0,
            isTranslation: false,
        }
        commit("ADD_MESSAGE", toastMessage)
    },
    clearMessage({ commit }, messageId: string) {
        commit("REMOVE_MESSAGE", messageId)
    },
    clearAllMessages({ commit }) {
        commit("CLEAR_ALL_MESSAGES")
    },
}

const getters: GetterTree<ToastMessageState, RootState> = {
    messages: (state) => state.messages,
}

const toastMessageModule: Module<ToastMessageState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default toastMessageModule
